// Custom sticker pricing (AUD, ex GST). Ready-made stickers do NOT use this file.
// Source of truth: customStickerTables.json, imported unchanged from the approved spreadsheets.
// Rules (approved by owner):
// - Circle/square (diameter or side): straight line between the two adjacent 25 mm table sizes (exact at 50, 75 … 500).
//   Rectangle/oval: bilinear blend of the four surrounding width × height table entries (exact at table sizes).
//   Equal width and height always uses the circle/square price. Under 50 mm → 50 mm price.
//   Custom shape: rectangle/oval base prices over the bounding box × 1.05 premium, $125 minimum (customShapePrice).
// - Total quantity = quantity per design × number of designs. Exact tier quantities use the exact spreadsheet
//   price; between two tiers the price is a straight line between the two tier prices (strictly increasing).
//   Under 500 the 500-tier price is scaled by quantity (same per-sticker price). Over 500 mm → quote.
//   Over 100,000 = 100,000-tier per-sticker price × quantity (all sticker types).
// - $99 minimum applies once per configured order line.
// - Sticker sheets: separate A6/A5/A4 price tables (see SHEET_SIZES), $125 minimum.
import TABLES from "./customStickerTables.json";

export const CUSTOM_CONFIG = {
  MIN_CHARGE: 99,
  MIN_DIM_MM: 20,
  MAX_DIM_MM: 500,
  MAX_KINDS: 100,
  WASTE: 0.3,
  // Internal costing inputs (not used in selling prices yet)
  COSTS: {
    rollWidthMm: 1520, usableWidthMm: 1500, rollLengthM: 45.7, rollCost: 210.6, rawMediaPerM2: 3.03,
    inkMlPerM2: 7, inkBladderMl: 2000, inkBladderCost: 500, inkPerM2: 1.75,
    printM2PerHour: 50, operatorWage: 40, gapMm: 5, lamination: false,
  },
};

export const PRICE_TABLES = TABLES;
const SIZES = TABLES.sizes;

// Adjacent table sizes around mm and the blend fraction between them.
function bracket(mm) {
  const v = Math.max(mm, SIZES[0]);
  const i = Math.max(0, SIZES.findIndex((s) => s >= v) - 1);
  const [a, b] = [SIZES[i], SIZES[i + 1]];
  return [a, b, Math.min(1, (v - a) / (b - a))];
}
const round2 = (v) => Math.round(v * 100) / 100;

// Exact at tiers; straight line between adjacent tiers; under the first tier the first tier's per-sticker price.
// Above the last tier: "unit" families charge the last tier's per-sticker price × quantity (no further discount).
function priceForQtyRaw(table, row, n) {
  const T = table.tiers;
  const last = T.length - 1;
  if (n <= T[0]) return (row[0] * n) / T[0];
  if (n > T[last]) return (row[last] / T[last]) * n;
  const j = T.findIndex((t) => t >= n);
  if (T[j] === n) return row[j];
  return row[j - 1] + ((row[j] - row[j - 1]) * (n - T[j - 1])) / (T[j] - T[j - 1]);
}

function priceForQty(table, row, n) {
  const T = table.tiers;
  const last = T.length - 1;
  if (n <= T[0]) return round2((row[0] * n) / T[0]);
  if (n > T[last]) return round2((row[last] / T[last]) * n);
  const j = T.findIndex((t) => t >= n);
  if (T[j] === n) return row[j];
  const [q0, q1, p0, p1] = [T[j - 1], T[j], row[j - 1], row[j]];
  return round2(p0 + ((p1 - p0) * (n - q0)) / (q1 - q0));
}

// Custom shape only: rectangle/oval base matrix (bilinear over the bounding box, same quantity rules)
// × 5% premium at full precision, then $125 minimum, rounded to cents. Isolated from all other types.
function customShapePrice(w, h, total) {
  const t = TABLES.customShape;
  const [w0, w1, fw] = bracket(w);
  const [h0, h1, fh] = bracket(h);
  const at = (x, y) => priceForQtyRaw(t, t.rows[`${x}x${y}`], total);
  const base = at(w0, h0) * (1 - fw) * (1 - fh) + at(w1, h0) * fw * (1 - fh) + at(w0, h1) * (1 - fw) * fh + at(w1, h1) * fw * fh;
  const withPremium = base * t.premium;
  const price = round2(Math.max(t.minCharge, withPremium));
  return { status: "ok", price, total, pricedSize: `custom shape ${Math.max(w, 50)} × ${Math.max(h, 50)} mm`, minimumApplied: withPremium < t.minCharge, minCharge: t.minCharge };
}

// Returns { status: "ok" | "quote" | "invalid", price, total, pricedSize, minimumApplied, message }
export function customStickerPrice({ type, width, height, qtyPerDesign, kinds }) {
  const C = CUSTOM_CONFIG;
  const w = Number(width);
  const h = Number(height);
  const q = Number(qtyPerDesign);
  const k = Number(kinds);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return { status: "invalid", message: "Enter your sticker size in millimetres." };
  if (Math.min(w, h) < C.MIN_DIM_MM) return { status: "invalid", message: `The smallest size we can cut is ${C.MIN_DIM_MM} mm.` };
  if (!Number.isInteger(q) || q < 1) return { status: "invalid", message: "Enter a whole-number quantity per design." };
  if (!Number.isInteger(k) || k < 1 || k > C.MAX_KINDS) return { status: "invalid", message: `Number of designs must be between 1 and ${C.MAX_KINDS}.` };
  const total = q * k;
  if (Math.max(w, h) > C.MAX_DIM_MM) return { status: "quote", total, message: `Stickers over ${C.MAX_DIM_MM} mm are priced by quote.` };

  let tablePrice;
  let pricedSize;
  if (type === "custom") {
    return customShapePrice(w, h, total);
  } else if (w === h) {
    const t = TABLES.circleSquare;
    const [a, b, f] = bracket(w);
    tablePrice = round2(priceForQty(t, t.rows[a], total) * (1 - f) + priceForQty(t, t.rows[b], total) * f);
    pricedSize = w < 50 ? "50 mm (our smallest price size)" : `${w} mm`;
  } else {
    const t = TABLES.rectangleOval;
    const [w0, w1, fw] = bracket(w);
    const [h0, h1, fh] = bracket(h);
    const at = (x, y) => priceForQty(t, t.rows[`${x}x${y}`], total);
    tablePrice = round2(
      at(w0, h0) * (1 - fw) * (1 - fh) + at(w1, h0) * fw * (1 - fh) + at(w0, h1) * (1 - fw) * fh + at(w1, h1) * fw * fh,
    );
    pricedSize = `${Math.max(w, 50)} × ${Math.max(h, 50)} mm`;
  }
  const price = Math.max(C.MIN_CHARGE, tablePrice);
  return { status: "ok", price, total, pricedSize, minimumApplied: tablePrice < C.MIN_CHARGE };
}

// Sticker sheets only: approved price tables per sheet size (AUD total order price, ex GST).
export const SHEET_SIZES = {
  A6: { label: "A6", mm: "105 × 148 mm", width: 105, height: 148, tiers: [[50, 143.5], [100, 187.96], [300, 365.13], [500, 525.1], [1000, 842.25], [2500, 1596.88]] },
  A5: { label: "A5", mm: "148 × 210 mm", width: 148, height: 210, tiers: [[50, 187.96], [100, 290.36], [300, 586.14], [500, 842.28], [1000, 1301.6], [2500, 2813.8]] },
  A4: { label: "A4", mm: "210 × 297 mm", width: 210, height: 297, tiers: [[10, 135.4], [50, 289.91], [100, 438.5], [300, 977.91], [500, 1297.65], [1000, 2307.45], [2500, 5437.7]] },
};
export const SHEET_MIN_CHARGE = 125;

// Exact at listed breaks; linear between breaks; below the first break and above the last break
// the first/last break's unit price × quantity (full precision). $125 minimum. Rounded to cents at the end.
export function stickerSheetPrice({ size, sheets }) {
  const table = SHEET_SIZES[size];
  const n = Number(sheets);
  if (!table) return { status: "invalid", message: "Choose a sheet size." };
  if (!Number.isInteger(n) || n < 1) return { status: "invalid", message: "Enter a whole number of sheets." };
  const t = table.tiers;
  const [q0, p0] = t[0];
  const [qL, pL] = t[t.length - 1];
  let raw;
  if (n <= q0) raw = (p0 / q0) * n;
  else if (n >= qL) raw = (pL / qL) * n;
  else {
    const j = t.findIndex(([q]) => q >= n);
    const [[qa, pa], [qb, pb]] = [t[j - 1], t[j]];
    raw = pa + ((pb - pa) * (n - qa)) / (qb - qa);
  }
  const price = Math.round(Math.max(SHEET_MIN_CHARGE, raw) * 100) / 100;
  return { status: "ok", price, total: n, pricedSize: `${table.label} (${table.mm})`, minimumApplied: raw < SHEET_MIN_CHARGE, minCharge: SHEET_MIN_CHARGE };
}
